/**
 * LegacyFunnelHub — single source of truth for the older TopRankPulse
 * funnels that pre-date the Agent OS rollout. Both new funnels (Rent +
 * Hero) embed this component so visitors who scroll past the agent
 * grid still find their way into the right tool funnel.
 *
 * If you build a new top-level funnel/landing, add ONE row here. Don't
 * re-paste link cards in 3 different files.
 */
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";

export const LEGACY_FUNNELS = [
  {
    slug: "snapshot", url: "/snapshot",
    title: "AI Snapshot — 9-bodový SEO audit za 30s",
    blurb: "Zadáš doménu, agent ti dá score + 9 konkrétnych fix-ov. Bezplatné.",
    tag: "Free",
  },
  {
    slug: "fix-my-seo", url: "/fix-my-seo",
    title: "Fix My SEO",
    blurb: "Audit + auto-generated action plan na opravu meta + content gaps.",
    tag: "Free",
  },
  {
    slug: "brand-monitor", url: "/brand-monitor",
    title: "Brand Monitor",
    blurb: "Tracking brand mentions cez ChatGPT, Perplexity, Gemini.",
    tag: "€29/mes",
  },
  {
    slug: "ai-simulator", url: "/ai-simulator",
    title: "AI Search Simulator",
    blurb: "Vidíš, ako ti ChatGPT odpovedá na otázky o tvojej značke.",
    tag: "Free",
  },
  {
    slug: "competitor-spy", url: "/competitor-spy",
    title: "Competitor Spy",
    blurb: "Sleduj 5 konkurentov — nové kampane, ceny, blogy.",
    tag: "€49/mes",
  },
  {
    slug: "blog-agent", url: "/blog-agent",
    title: "Blog Agent",
    blurb: "10 blog článkov mesačne, AI-optimized, GEO-targeted.",
    tag: "€69/mes",
  },
  {
    slug: "ads-creator", url: "/ads-creator",
    title: "Ads Creator",
    blurb: "Meta + Google Ads texty + creatives z 1 produktu za 60s.",
    tag: "€49/mes",
  },
  {
    slug: "revenue-leak", url: "/revenue-leak",
    title: "Revenue Leak Audit",
    blurb: "Identifikuje, kde ti uniká €X tisíc mesačne z funnelu.",
    tag: "Free",
  },
  {
    slug: "cookie-audit", url: "/cookie-audit",
    title: "Cookie + GDPR Audit",
    blurb: "Auto-scan + DPA template generator.",
    tag: "Free",
  },
  {
    slug: "tools", url: "/tools",
    title: "Tools Hub",
    blurb: "30+ AI nástrojov — od meta description generátora po image alt-text.",
    tag: "Free",
  },
  {
    slug: "lifetime", url: "/lifetime",
    title: "Lifetime Deal",
    blurb: "€297 raz a navždy. Iba 100 ks.",
    tag: "Deal",
  },
  {
    slug: "agent-os-agency", url: "/agent-os/agency",
    title: "Agency Plan",
    blurb: "Pre agentúry — manage 10+ klientov, white-label, multi-tenant.",
    tag: "€349/mes",
  },
];

export default function LegacyFunnelHub({ title = "Ostatné funnels & nástroje", limit = null }) {
  const items = limit ? LEGACY_FUNNELS.slice(0, limit) : LEGACY_FUNNELS;
  return (
    <section className="px-6 py-16 border-t border-gray-900 bg-neutral-950" data-testid="legacy-funnel-hub">
      <div className="max-w-6xl mx-auto">
        <p className="text-[11px] uppercase tracking-[0.2em] font-black text-amber-400 mb-3">
          Naše ostatné nástroje
        </p>
        <h2 className="text-3xl sm:text-4xl font-black mb-3">{title}</h2>
        <p className="text-base text-gray-400 mb-10 max-w-2xl">
          Predtým, než sme spustili 14 AI agentov, sme postavili tieto samostatné nástroje. Stále fungujú. Stále zadarmo (väčšina).
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {items.map((f) => (
            <Link key={f.slug} to={f.url}
              data-testid={`legacy-funnel-${f.slug}`}
              className="border border-gray-800 p-4 hover:border-amber-500 transition-colors group block">
              <div className="flex items-start justify-between gap-2 mb-2">
                <h3 className="font-black text-base leading-tight group-hover:text-amber-400">
                  {f.title}
                </h3>
                <span className={`text-[10px] font-black uppercase tracking-widest shrink-0 px-2 py-0.5 ${
                  f.tag === "Free" ? "bg-emerald-500/20 text-emerald-300" :
                  f.tag === "Deal" ? "bg-amber-500/20 text-amber-300" :
                  "border border-gray-800 text-gray-500"
                }`}>
                  {f.tag}
                </span>
              </div>
              <p className="text-sm text-gray-400 leading-relaxed mb-2">{f.blurb}</p>
              <p className="text-[11px] text-amber-400 group-hover:underline inline-flex items-center gap-1">
                Otvoriť <ArrowUpRight className="w-3 h-3" />
              </p>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
